function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { FC, Ref, useState, useCallback, useRef, MouseEventHandler, ReactElement } from 'react';
import cn from 'classnames';
import { RenderOverride, useRenderOverride } from '../../lib/render-override';
import { useUpdateEffect } from '../../hooks/useUpdateEffect';
import { useUniqId } from '../../hooks/useUniqId';
import { IWithControlProps, withControl } from '../withControl/withControl';
import { withAutoFocus } from '../withAutoFocus/withAutoFocus';
import { withControl as withControlDesktop } from '../withControl/withControl@desktop';
import { withDebounceInput } from '../withDebounceInput';
import { IWithControlProps as IWithControlPropsDesktop } from '../withControl/withControl@desktop';
import { TextareaBox as Box } from './Box/Textarea-Box';
import { TextareaHint as Hint } from './Hint/Textarea-Hint';
import ControlOriginal, { ITextareaAutosizeProps } from './Control/Textarea-Control';
import { TextareaWrap as Wrap } from './Wrap/Textarea-Wrap';
import { TextareaLabel as Label } from './Label/Textarea-Label';
import { TextareaSecondLabel as SecondLabel } from './SecondLabel/Textarea-SecondLabel';
import { TextareaIcon as Icon } from './Icon/Textarea-Icon';
import { IIconProps } from '../Icon/Icon';
require("./Textarea.css");
var styles = {
  "Root": "Textarea-module-Root_3HB",
  "hasFullWidth": "Textarea-module-hasFullWidth_1iw",
  "iconRight": "Textarea-module-iconRight_22m",
  "Control": "Textarea-module-Control_26q",
  "iconLeft": "Textarea-module-iconLeft_Rb3",
  "Label": "Textarea-module-Label_2hQ",
  "hasIndependentLabel": "Textarea-module-hasIndependentLabel_3HL",
  "hasValue": "Textarea-module-hasValue_1Lx",
  "focused": "Textarea-module-focused_2gi",
  "Box": "Textarea-module-Box_-91",
  "hovered": "Textarea-module-hovered_2i2",
  "sSize": "Textarea-module-sSize_An6",
  "mSize": "Textarea-module-mSize_4Lv",
  "Wrap": "Textarea-module-Wrap_2ON",
  "defaultVariant": "Textarea-module-defaultVariant_3xZ",
  "Icon": "Textarea-module-Icon_2AF",
  "errorState": "Textarea-module-errorState_1Q9",
  "Hint": "Textarea-module-Hint_uBG",
  "disabled": "Textarea-module-disabled_3iV",
  "filledVariant": "Textarea-module-filledVariant_3cC"
};
export const TextareaVariants = {
  default: 'default',
  filled: 'filled'
};
export const TextareaSizes = {
  s: 's',
  m: 'm'
};
/**
 * Многострочное текстовое поле.
 * @param {ITextareaProps} props
 */
const TextareaBase = ({
  disabled,
  focused,
  hovered,
  onMouseEnter,
  onMouseLeave,
  innerRef,
  iconLeft,
  iconRight,
  wrapRef,
  variant = TextareaVariants.default,
  className: textareaClassName,
  classNameField,
  // Извлекаем свойства, т.к. они не нужны на DOM узле
  pressed: _pressed,
  size = TextareaSizes.m,
  hint: htmlHint,
  hasIndependentLabel,
  state,
  title,
  hasFullWidth,
  label,
  showCharactersCounter,
  maxRows = 20,
  minRows = 2,
  placeholder = 'Placeholder',
  renderControl,
  ...props
}) => {
  const className = cn(styles.Root, styles[`${variant}Variant`], styles[`${state}State`], styles[`${size}Size`], {
    [styles.hovered]: hovered,
    [styles.focused]: focused,
    [styles.hasFullWidth]: hasFullWidth,
    [styles.hasIndependentLabel]: hasIndependentLabel,
    [styles.hasValue]: Boolean(props.value && props.value.length),
    [styles.iconRight]: Boolean(iconRight),
    [styles.iconLeft]: Boolean(iconLeft),
    [styles.disabled]: disabled
  }, textareaClassName);
  const Control = useRenderOverride(ControlOriginal, renderControl);
  const [hint, setHint] = useState(htmlHint);
  const [hintLeave, setHintLeave] = useState(false);
  const prevHint = useRef(htmlHint);
  const hintId = useUniqId('hint');
  const textareaId = useUniqId('textarea');
  useUpdateEffect(() => {
    if (htmlHint) {
      setHint(htmlHint);
    } else if (prevHint.current) {
      setHintLeave(true);
    }
    prevHint.current = htmlHint;
  }, [htmlHint]);
  const onAnimationEnd = useCallback(() => {
    if (!htmlHint) {
      setHint('');
      setHintLeave(false);
    }
  }, [htmlHint]);
  return /*#__PURE__*/React.createElement("span", {
    ref: innerRef,
    className: className,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    title: title
  }, !hasIndependentLabel && Boolean(label) && /*#__PURE__*/React.createElement(SecondLabel, _extends({
    title: label,
    showCharactersCounter: showCharactersCounter
  }, props, {
    id: textareaId
  })), /*#__PURE__*/React.createElement(Wrap, {
    innerRef: wrapRef,
    className: styles.Wrap
  }, hasIndependentLabel && Boolean(label) && /*#__PURE__*/React.createElement(Label, _extends({
    title: label
  }, props, {
    id: textareaId,
    className: styles.Label
  })), iconLeft && /*#__PURE__*/React.createElement(Icon, {
    side: "left",
    component: iconLeft,
    className: styles.Icon
  }), iconRight && /*#__PURE__*/React.createElement(Icon, {
    side: "right",
    component: iconRight,
    className: styles.Icon
  }), /*#__PURE__*/React.createElement(Control, _extends({}, props, {
    className: cn(styles.Control, classNameField),
    placeholder: !hasIndependentLabel ? placeholder : undefined,
    minRows: minRows,
    maxRows: maxRows,
    style: {
      boxSizing: 'border-box'
    },
    "aria-invalid": state === 'error',
    disabled: disabled,
    "aria-describedby": hint ? hintId : undefined,
    id: textareaId
  })), /*#__PURE__*/React.createElement(Box, {
    className: styles.Box
  })), hint && !hasIndependentLabel && /*#__PURE__*/React.createElement(Hint, {
    leave: hintLeave,
    onAnimationEnd: onAnimationEnd,
    id: hintId,
    className: styles.Hint
  }, hint));
};
if (process.env.NODE_ENV !== 'production') {
  TextareaBase.displayName = 'Textarea';
}

/**
 * @deprecated вместо этого используйте {@link FieldText}
 */
export const Textarea = withDebounceInput(withAutoFocus(withControlDesktop(withControl(TextareaBase))));